import PropTypes from 'prop-types';
import { ArrayField, Datagrid, FunctionField, RecordContextProvider, ResourceContextProvider, useRecordContext, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CustomBooleanField } from '@/components/global/fields/CustomBooleanField.jsx';
import { ParameterSubsetTypeField } from '@/components/parameter/fields/ParameterSubsetTypeField.jsx';
import { ParameterSubsetTargetsField } from '@/components/parameter/fields/ParameterSubsetTargetsField.jsx';
import { ParameterSubsetValueField } from '@/components/parameter/fields/ParameterSubsetValueField.jsx';
import { ParameterNestingCountField } from '@/components/parameter/fields/ParameterNestingCountField.jsx';
import { hasNestedSubsets } from '@/helpers/parameter/parameterExceptions.js';
import { CustomDatagridBody } from '@/components/global/list/CustomDatagridRow.jsx';
import { parameterExceptionListStyles, parameterExceptionNestedStyles } from '@/themes/commonStyles';

const StyledExceptionList = styled(Box)(parameterExceptionListStyles);

const StyledNestedExceptionList = styled(Box, { shouldForwardProp: prop => prop !== 'depth' })(({ depth }) => parameterExceptionNestedStyles(depth));

const isRowExpandable = record => hasNestedSubsets(record);

const ParameterExceptionNestedList = ({ depth }) => {
    const record = useRecordContext();
    const subsets = Array.isArray(record?.subsets) ? record.subsets : [];
    const resource = `parameter-exceptions-${record?.id}`;

    if (subsets.length === 0) {
        return null;
    }

    return (
        <StyledNestedExceptionList depth={depth}>
            <ParameterExceptionList subsets={subsets} resource={resource} depth={depth} />
        </StyledNestedExceptionList>
    );
};

ParameterExceptionNestedList.propTypes = {
    depth: PropTypes.number.isRequired,
};

export const ParameterExceptionList = ({ subsets, resource, depth = 0 }) => {
    const translate = useTranslate();

    return (
        <ResourceContextProvider value={resource}>
            <RecordContextProvider value={{ id: resource, subsets }}>
                <StyledExceptionList>
                    {depth > 0 ? (
                        <Typography variant='body2' className='parameter-exception-nested-title'>
                            {translate('resources.parameter.fields.subsetForSubset')}
                        </Typography>
                    ) : null}
                    <ArrayField source='subsets' perPage={10000}>
                        <Datagrid
                            body={<CustomDatagridBody />}
                            bulkActionButtons={false}
                            expandSingle={false}
                            rowClick='expand'
                            expand={<ParameterExceptionNestedList depth={depth + 1} />}
                            isRowExpandable={isRowExpandable}
                            sx={{
                                '& .RaDatagrid-expandIconCell, & .RaDatagrid-headerCell:first-of-type': {
                                    display: 'none',
                                },
                            }}
                        >
                            <FunctionField
                                source='type'
                                label={translate('resources.parameter.fields.subsetType')}
                                sortable={false}
                                render={() => <ParameterSubsetTypeField />}
                            />
                            <FunctionField
                                source='subset'
                                label={translate('resources.parameter.fields.subset')}
                                sortable={false}
                                render={() => <ParameterSubsetTargetsField />}
                            />
                            <FunctionField
                                source='value'
                                label={translate('resources.parameter.fields.value')}
                                sortable={false}
                                render={() => <ParameterSubsetValueField />}
                            />
                            <CustomBooleanField source='isSignificant' label={translate('resources.parameter.fields.isSignificant')} sortable={false} />
                            <CustomBooleanField source='isActive' label={translate('resources.parameter.fields.isActive')} sortable={false} />
                            <FunctionField
                                source='nestings'
                                label={translate('resources.parameter.fields.nestings')}
                                sortable={false}
                                render={() => <ParameterNestingCountField />}
                            />
                        </Datagrid>
                    </ArrayField>
                </StyledExceptionList>
            </RecordContextProvider>
        </ResourceContextProvider>
    );
};

ParameterExceptionList.propTypes = {
    subsets: PropTypes.arrayOf(PropTypes.object).isRequired,
    resource: PropTypes.string.isRequired,
    depth: PropTypes.number,
};
